import { z } from 'zod';

import { UiLanguage } from './languages.js';

/**
 * Accounts (docs/accounts-classes-plan.md, ADR 0014/0015). Anyone may sign up; teachers are
 * approved by an admin; a student under 16 needs a parent's consent before the account can join
 * classes.
 */
export const UserRole = z.enum(['student', 'teacher', 'admin']);
export type UserRole = z.infer<typeof UserRole>;

export const ConsentStatus = z.enum(['not-needed', 'pending', 'granted']);
export type ConsentStatus = z.infer<typeof ConsentStatus>;

export const TeacherRequest = z.enum(['pending', 'rejected']);
export type TeacherRequest = z.infer<typeof TeacherRequest>;

/** GDPR Art. 8 as adopted in Romania: below this age a parent consents. */
export const CONSENT_AGE = 16;

export const MIN_PASSWORD = 8;

export const Email = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'invalid email');

export const Password = z.string().min(MIN_PASSWORD).max(200);

export const DisplayName = z.string().trim().min(1).max(40);

/** Birth month, "YYYY-MM": enough to know the age, less than a birth date. */
export const BirthMonth = z.string().regex(/^(19|20)\d{2}-(0[1-9]|1[0-2])$/, 'invalid month');

/**
 * Age in whole years on `today`, assuming the birthday falls on the last day of the month so a
 * student is never counted older than they are.
 */
export function ageFrom(birthMonth: string, today: Date = new Date()): number {
  const [y, m] = birthMonth.split('-').map(Number) as [number, number];
  const ty = today.getUTCFullYear();
  const tm = today.getUTCMonth() + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const hadBirthday = tm > m || (tm === m && today.getUTCDate() >= lastDay);
  return ty - y - (hadBirthday ? 0 : 1);
}

export const needsConsent = (birthMonth: string, today?: Date) =>
  ageFrom(birthMonth, today) < CONSENT_AGE;

/** The logged-in user, as the browser sees them. */
export const Me = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: UserRole,
  teacherRequest: TeacherRequest.nullable(),
  emailVerified: z.boolean(),
  consent: ConsentStatus,
  /** Masked, for the "we emailed your parent" banner: "p•••@example.com". */
  parentEmail: z.string().nullable(),
  uiLang: UiLanguage,
  /** Email verified and consent not pending: may join classes, see homework and class sessions. */
  active: z.boolean(),
  createdAt: z.string(),
});
export type Me = z.infer<typeof Me>;

export const MeResponse = z.object({
  user: Me.nullable(),
  /** Anonymous work moved into the account by this request (login, sign-up). */
  claimed: z.object({ projects: z.number(), points: z.number() }).optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const SignupRequest = z
  .object({
    email: Email,
    password: Password,
    displayName: DisplayName,
    birthMonth: BirthMonth,
    parentEmail: Email.optional(),
    uiLang: UiLanguage,
    wantsTeacher: z.boolean().default(false),
    acceptPrivacy: z.literal(true),
  })
  .refine((b) => !b.parentEmail || b.parentEmail !== b.email, {
    message: 'parent email must differ',
    path: ['parentEmail'],
  });
export type SignupRequest = z.input<typeof SignupRequest>;

export const LoginRequest = z.object({ email: Email, password: z.string().min(1).max(200) });
export type LoginRequest = z.infer<typeof LoginRequest>;

export const TokenRequest = z.object({ token: z.string().min(16).max(128) });

export const ForgotRequest = z.object({ email: Email, uiLang: UiLanguage.optional() });

export const ResetRequest = z.object({ token: z.string().min(16).max(128), password: Password });

export const UpdateAccountRequest = z.object({
  displayName: DisplayName.optional(),
  uiLang: UiLanguage.optional(),
});

export const ChangePasswordRequest = z.object({
  current: z.string().min(1).max(200),
  next: Password,
});

export const DeleteAccountRequest = z.object({ password: z.string().min(1).max(200) });

/** What a parent sees behind the consent link. */
export const ConsentInfo = z.object({
  childName: z.string(),
  childEmail: z.string(),
  status: ConsentStatus,
  uiLang: UiLanguage,
});
export type ConsentInfo = z.infer<typeof ConsentInfo>;

export const ConsentDecision = z.object({ decision: z.enum(['grant', 'deny']) });

/** A row in the admin's user list. */
export const AdminUser = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: UserRole,
  teacherRequest: TeacherRequest.nullable(),
  emailVerified: z.boolean(),
  consent: ConsentStatus,
  disabled: z.boolean(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
});
export type AdminUser = z.infer<typeof AdminUser>;

export const AdminUserFilter = z.enum([
  'all',
  'teacher-requests',
  'teachers',
  'admins',
  'disabled',
]);
export type AdminUserFilter = z.infer<typeof AdminUserFilter>;

export const AdminUserList = z.object({ users: z.array(AdminUser), total: z.number() });
export type AdminUserList = z.infer<typeof AdminUserList>;

export const AdminUserUpdate = z.object({
  role: UserRole.optional(),
  teacherRequest: z.enum(['approve', 'reject']).optional(),
  disabled: z.boolean().optional(),
});
export type AdminUserUpdate = z.infer<typeof AdminUserUpdate>;

/** "pa•••@example.com" */
export function maskEmail(email: string): string {
  const [local = '', domain = ''] = email.split('@');
  return `${local.slice(0, Math.min(2, local.length))}•••@${domain}`;
}
